import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  IconButton,
  Button,
  Chip,
  Paper,
  TextField,
  List,
  ListItem,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Fab,
} from "@mui/material";
import {
  Notes as NotesIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PushPin as PinIcon,
  Sort as SortIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import useClientLogData from "../../../../../hooks/useClientLogData";
import useEvents from "../../../../../hooks/useEvents";
import moment from "moment";
import { nanoid } from "nanoid";

// Shared styled components (consistent with other tabs)
const StyledNotesCard = styled(Card)(({ theme }) => ({
  height: "auto",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  transition: "box-shadow 0.3s ease",
  "&:hover": {
    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
  },
}));

const NotesHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const NotesContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
  paddingTop: theme.spacing(2),
}));

const ActionButton = styled(Button)(() => ({
  borderRadius: 20,
  padding: "8px 20px",
  textTransform: "none",
  fontWeight: 600,
}));

const NoteCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "isPinned",
})(({ theme, isPinned }) => ({
  marginBottom: theme.spacing(2),
  border: isPinned
    ? `2px solid ${theme.palette.warning.main}`
    : `1px solid ${theme.palette.divider}`,
  backgroundColor: isPinned ? theme.palette.warning.light + "0A" : "white",
  borderRadius: theme.spacing(1.5),
  transition: "all 0.2s ease",
  "&:hover": {
    boxShadow: theme.shadows[4],
  },
}));

// Enhanced Notes Tab Component
const EnhancedNotesTab = ({ client }) => {
  const [notes, setNotes] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [filterCategory, setFilterCategory] = React.useState("all");
  const [sortBy, setSortBy] = React.useState("newest");
  const [openDialog, setOpenDialog] = React.useState(false);
  const [selectedNote, setSelectedNote] = React.useState(null);
  const [newNote, setNewNote] = React.useState({
    title: "",
    content: "",
    category: "general",
    pinned: false,
  });

  const events = useEvents();

  // Handle snapshot updates from the logs collection
  const onSnapshot = React.useCallback((snapshot) => {
    const logEntries = snapshot.docs.map((doc) => {
      const data = doc.data();
      // Parse the stored log data - extract title, category and pinned status from the body if available
      let content = data.body || "";
      let title = "";
      let category = "general";
      let pinned = false;

      try {
        // Check if the body is JSON (our enhanced format)
        const parsedData = JSON.parse(content);
        if (parsedData && typeof parsedData === "object") {
          title = parsedData.title || "";
          content = parsedData.content || "";
          category = parsedData.category || "general";
          pinned = parsedData.pinned || false;
        }
      } catch (e) {
        // If it's not valid JSON, treat the entire body as content
        // This handles legacy notes
        title = content.split("\n")[0] || "Note";
        // Don't reassign content to itself
      }

      return {
        id: data.id,
        title: title,
        content: content,
        category: category,
        pinned: pinned,
        date: data.createdOn?.toDate() || new Date(),
        author: "Coach", // Default author
        tags: content.match(/#\w+/g) || [],
      };
    });

    setNotes(logEntries);
  }, []);

  // Initialize the log data hook
  const { editLogData, deleteLogData } = useClientLogData({
    clientId: client?.uid,
    onSnapshot: onSnapshot,
  });

  const categories = [
    { value: "all", label: "All Notes" },
    { value: "general", label: "General" },
    { value: "consultation", label: "Consultation" },
    { value: "progress", label: "Progress" },
    { value: "nutrition", label: "Nutrition" },
    { value: "training", label: "Training" },
    { value: "medical", label: "Medical" },
  ];

  const sortOptions = [
    { value: "newest", label: "Newest First" },
    { value: "oldest", label: "Oldest First" },
    { value: "pinned", label: "Pinned First" },
    { value: "category", label: "By Category" },
  ];

  const filteredAndSortedNotes = React.useMemo(() => {
    const filtered = notes.filter((note) => {
      const matchesSearch =
        note.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        note.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (note.tags &&
          note.tags.some((tag) =>
            tag.toLowerCase().includes(searchTerm.toLowerCase()),
          ));
      const matchesCategory =
        filterCategory === "all" || note.category === filterCategory;
      return matchesSearch && matchesCategory;
    });

    filtered.sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return new Date(b.date) - new Date(a.date);
        case "oldest":
          return new Date(a.date) - new Date(b.date);
        case "pinned":
          return (
            (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0) ||
            new Date(b.date) - new Date(a.date)
          );
        case "category":
          return (
            a.category.localeCompare(b.category) ||
            new Date(b.date) - new Date(a.date)
          );
        default:
          return 0;
      }
    });

    return filtered;
  }, [notes, searchTerm, filterCategory, sortBy]);

  const getCategoryColor = (category) => {
    switch (category) {
      case "consultation":
        return "primary";
      case "progress":
        return "success";
      case "nutrition":
        return "info";
      case "training":
        return "warning";
      case "medical":
        return "error";
      default:
        return "default";
    }
  };

  const pinnedNotes = notes.filter((note) => note.pinned);
  const totalNotes = notes.length;
  const recentNotes = notes.filter(
    (note) =>
      Date.now() - new Date(note.date).getTime() < 7 * 24 * 60 * 60 * 1000,
  );

  const handleSaveNote = async () => {
    // Create the structured JSON data to store in the body field
    const noteData = {
      title: newNote.title,
      content: newNote.content,
      category: newNote.category,
      pinned: newNote.pinned,
    };

    const bodyContent = JSON.stringify(noteData);

    const currentDate = new Date();

    if (selectedNote) {
      // Edit existing note
      await editLogData({
        id: selectedNote.id,
        body: bodyContent,
        createdOn: new Date(selectedNote.date),
        updatedOn: currentDate,
      });

      events.logButtonClick({
        buttonName: "Update Note",
        pageName: "Client Notes",
        tabName: client?.uid,
      });
    } else {
      // Add new note
      await editLogData({
        id: nanoid(),
        body: bodyContent,
        createdOn: currentDate,
        updatedOn: currentDate,
      });

      events.logButtonClick({
        buttonName: "Add Note",
        pageName: "Client Notes",
        tabName: client?.uid,
      });
    }

    setOpenDialog(false);
    setSelectedNote(null);
    setNewNote({ title: "", content: "", category: "general", pinned: false });
  };

  const handleEditNote = (note) => {
    setSelectedNote(note);
    setNewNote({
      title: note.title,
      content: note.content,
      category: note.category,
      pinned: note.pinned,
    });
    setOpenDialog(true);

    events.logButtonClick({
      buttonName: "Edit Note",
      pageName: "Client Notes",
      tabName: client?.uid,
    });
  };

  const handleDeleteNote = (note) => {
    if (window.confirm("Are you sure you want to delete this note?")) {
      deleteLogData(note.id);

      events.logButtonClick({
        buttonName: "Delete Note",
        pageName: "Client Notes",
        tabName: client?.uid,
      });
    }
  };

  const togglePin = async (note) => {
    // Update the note with toggled pinned status
    const noteData = {
      title: note.title,
      content: note.content,
      category: note.category,
      pinned: !note.pinned,
    };

    const bodyContent = JSON.stringify(noteData);

    await editLogData({
      id: note.id,
      body: bodyContent,
      createdOn: new Date(note.date),
      updatedOn: new Date(),
    });

    events.logButtonClick({
      buttonName: note.pinned ? "Unpin Note" : "Pin Note",
      pageName: "Client Notes",
      tabName: client?.uid,
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* Notes Overview */}
        <Grid item xs={12} md={4}>
          <StyledNotesCard>
            <NotesHeader>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <NotesIcon color="primary" sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight="bold">
                  Notes Overview
                </Typography>
              </Stack>
              <IconButton size="small">
                <MoreIcon />
              </IconButton>
            </NotesHeader>
            <NotesContent>
              <Stack spacing={3}>
                {/* Statistics */}
                <Box>
                  <Typography
                    variant="h2"
                    fontWeight="bold"
                    color="primary"
                    align="center"
                  >
                    {totalNotes}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                  >
                    Total Notes
                  </Typography>
                </Box>

                <Divider />

                {notes.length > 0 ? (
                  <>
                    {/* Quick Stats */}
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box textAlign="center">
                          <Typography
                            variant="h5"
                            fontWeight="bold"
                            color="warning.main"
                          >
                            {pinnedNotes.length}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Pinned
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box textAlign="center">
                          <Typography
                            variant="h5"
                            fontWeight="bold"
                            color="success.main"
                          >
                            {recentNotes.length}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            This Week
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    <Divider />

                    {/* Category Breakdown */}
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        By Category
                      </Typography>
                      <Stack spacing={1}>
                        {categories.slice(1).map((cat) => {
                          const count = notes.filter(
                            (note) => note.category === cat.value,
                          ).length;
                          return (
                            <Stack
                              key={cat.value}
                              direction="row"
                              justifyContent="space-between"
                            >
                              <Chip
                                label={cat.label}
                                color={getCategoryColor(cat.value)}
                                size="small"
                                variant="outlined"
                              />
                              <Typography variant="body2" fontWeight="bold">
                                {count}
                              </Typography>
                            </Stack>
                          );
                        })}
                      </Stack>
                    </Box>
                  </>
                ) : (
                  <Paper
                    sx={{
                      p: 3,
                      textAlign: "center",
                      bgcolor: "grey.50",
                      borderStyle: "dashed",
                      borderWidth: 2,
                      borderColor: "grey.300",
                    }}
                  >
                    <NotesIcon
                      sx={{ fontSize: 48, color: "grey.400", mb: 2 }}
                    />
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      No notes yet
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Start documenting client progress
                    </Typography>
                  </Paper>
                )}

                <ActionButton
                  variant="contained"
                  startIcon={<AddIcon />}
                  fullWidth
                  onClick={() => setOpenDialog(true)}
                >
                  New Note
                </ActionButton>
              </Stack>
            </NotesContent>
          </StyledNotesCard>
        </Grid>

        {/* Notes List */}
        <Grid item xs={12} md={8}>
          <StyledNotesCard>
            <NotesHeader>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <NotesIcon color="primary" sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight="bold">
                  Client Notes
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <ActionButton
                  variant="outlined"
                  size="small"
                  startIcon={<FilterIcon />}
                  onClick={() => {
                    if (filterCategory === "all") {
                      setFilterCategory("general");
                    } else {
                      setFilterCategory("all");
                    }
                  }}
                >
                  {filterCategory === "all" ? "All Categories" : "Filtered"}
                </ActionButton>
                <ActionButton
                  variant="outlined"
                  size="small"
                  startIcon={<SortIcon />}
                  onClick={() => {
                    const currentIndex = sortOptions.findIndex(
                      (opt) => opt.value === sortBy,
                    );
                    const nextIndex = (currentIndex + 1) % sortOptions.length;
                    setSortBy(sortOptions[nextIndex].value);
                  }}
                >
                  {sortOptions.find((opt) => opt.value === sortBy)?.label}
                </ActionButton>
              </Stack>
            </NotesHeader>
            <NotesContent>
              {/* Search and Filters */}
              <Stack spacing={2} sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  placeholder="Search notes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ borderRadius: 2 }}
                />

                <Stack direction="row" spacing={2}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      label="Category"
                    >
                      {categories.map((cat) => (
                        <MenuItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Sort By</InputLabel>
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      label="Sort By"
                    >
                      {sortOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Stack>

              {/* Notes List */}
              {filteredAndSortedNotes.length > 0 ? (
                <List sx={{ p: 0 }}>
                  {filteredAndSortedNotes.map((note) => (
                    <NoteCard key={note.id} isPinned={note.pinned}>
                      <ListItem
                        sx={{ p: 2 }}
                        secondaryAction={
                          <Stack direction="row" spacing={1}>
                            <IconButton
                              size="small"
                              onClick={() => togglePin(note)}
                              color={note.pinned ? "warning" : "default"}
                            >
                              <PinIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleEditNote(note)}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteNote(note)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                        }
                      >
                        <ListItemText
                          primary={
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ mb: 1 }}
                            >
                              <Typography variant="h6" fontWeight="bold">
                                {note.title}
                              </Typography>
                              <Chip
                                label={
                                  categories.find(
                                    (c) => c.value === note.category,
                                  )?.label || note.category
                                }
                                color={getCategoryColor(note.category)}
                                size="small"
                              />
                              {note.pinned && (
                                <Chip
                                  icon={<PinIcon />}
                                  label="Pinned"
                                  color="warning"
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Stack>
                          }
                          secondary={
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.primary"
                                paragraph
                                sx={{ whiteSpace: "pre-wrap" }}
                              >
                                {note.content}
                              </Typography>
                              {note.tags && note.tags.length > 0 && (
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  sx={{ mb: 1 }}
                                >
                                  {note.tags.map((tag, index) => (
                                    <Chip
                                      key={index}
                                      label={tag}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                    />
                                  ))}
                                </Stack>
                              )}
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  by Coach
                                </Typography>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={0.5}
                                >
                                  <ScheduleIcon
                                    fontSize="small"
                                    color="disabled"
                                  />
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {moment(note.date).format(
                                      "MMM D, YYYY h:mm A",
                                    )}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </Box>
                          }
                        />
                      </ListItem>
                    </NoteCard>
                  ))}
                </List>
              ) : (
                <Paper
                  sx={{
                    p: 3,
                    textAlign: "center",
                    bgcolor: "grey.50",
                    borderStyle: "dashed",
                    borderWidth: 2,
                    borderColor: "grey.300",
                  }}
                >
                  <NotesIcon sx={{ fontSize: 48, color: "grey.400", mb: 2 }} />
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {searchTerm || filterCategory !== "all"
                      ? "No notes match your filters"
                      : "No notes found"}
                  </Typography>
                  <ActionButton
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenDialog(true)}
                  >
                    Create First Note
                  </ActionButton>
                </Paper>
              )}
            </NotesContent>
          </StyledNotesCard>
        </Grid>
      </Grid>

      {/* Add/Edit Note Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{selectedNote ? "Edit Note" : "New Note"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Title"
              value={newNote.title}
              onChange={(e) =>
                setNewNote({ ...newNote, title: e.target.value })
              }
            />

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={newNote.category}
                onChange={(e) =>
                  setNewNote({ ...newNote, category: e.target.value })
                }
                label="Category"
              >
                {categories.slice(1).map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Content"
              multiline
              rows={6}
              value={newNote.content}
              onChange={(e) =>
                setNewNote({ ...newNote, content: e.target.value })
              }
              placeholder="Write your note here... Use #tags for better organization"
            />

            <Stack direction="row" alignItems="center" spacing={2}>
              <Button
                variant={newNote.pinned ? "contained" : "outlined"}
                startIcon={<PinIcon />}
                onClick={() =>
                  setNewNote({ ...newNote, pinned: !newNote.pinned })
                }
              >
                {newNote.pinned ? "Pinned" : "Pin Note"}
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSaveNote}
            variant="contained"
            disabled={!newNote.title.trim() || !newNote.content.trim()}
          >
            {selectedNote ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button for empty state */}
      {notes.length === 0 && (
        <Fab
          color="primary"
          aria-label="add note"
          onClick={() => setOpenDialog(true)}
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 1000,
          }}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  );
};

export default EnhancedNotesTab;
